import { useTranslation } from 'react-i18next'
import { fileStatus } from '../../media'

export const useMediaListService = ({ media, onReplaceFileInGroup, onAddFileToGroup, onValidationError }) => {
  const { t } = useTranslation()

  const validateFileForPlacement = async (file, requiredPlacementType) => {
    const errorMessages = []
    
    if (file.type.startsWith('image/')) {
      await new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const { width, height } = img
          const aspectRatio = height / width
          const filePlacementType = aspectRatio > 1.4 ? 'reel' : 'feed'
    
          if (filePlacementType !== requiredPlacementType) {
            errorMessages.push(
              t(`Recommendations.campaign.setup.media.errors.image.${requiredPlacementType}`, { fileName: file.name })
            )
          }
          resolve()
        }
        img.src = URL.createObjectURL(file)
      })
    } else if (file.type.startsWith('video/')) {
      await new Promise((resolve) => {
        const video = document.createElement('video')
        video.onloadedmetadata = () => {
          const { videoWidth, videoHeight } = video
          const aspectRatio = videoHeight / videoWidth
          const filePlacementType = aspectRatio > 1.4 ? 'reel' : 'feed'
    
          if (filePlacementType !== requiredPlacementType) {
            errorMessages.push(
              t(`Recommendations.campaign.setup.media.errors.video.${requiredPlacementType}`, { fileName: file.name })
            )
          }
          resolve()
        }
        video.src = URL.createObjectURL(file)
      })
    }
    
    return errorMessages.length > 0 ? errorMessages : null
  }

  const validateFileTypeForGroup = (file, targetFileId, isReplace = false) => {
    const newFileType = file.type.startsWith('image/') ? 'image' : 'video'
    
    // Find target file in media
    const targetFile = media.find(f => f.id === targetFileId)
    if (!targetFile) {
      return null
    }
    
    // Get placement_group_id from target file
    const groupId = targetFile.placement_group_id
    
    if (!groupId) {
      // Standalone file (no group) - when adding, we're creating a new group
      // Check that new file type matches target file type
      if (!isReplace) {
        const targetFileType = 
        (targetFile.type && targetFile.type.startsWith('image/')) || 
        (targetFile.file && targetFile.file.type && targetFile.file.type.startsWith('image/'))
          ? 'image' 
          : 'video'
        
        if (newFileType !== targetFileType) {
          return [t('Recommendations.campaign.setup.media.errors.formatMismatch', { fileName: file.name })]
        }
      }
      // If replacing standalone file, no validation needed
      return null
    }
    
    let filesInGroup = media.filter(f => f.placement_group_id === groupId)
    
    // If replacing, exclude the file being replaced from validation
    if (isReplace) {
      filesInGroup = filesInGroup.filter(f => f.id !== targetFileId)
    }
    
    // Check types of all files in group
    const hasImages = filesInGroup.some(f => {
      // Check both file.type (for new files) and f.type (for existing files)
      return (f.type && f.type.startsWith('image/')) || 
             (f.file && f.file.type && f.file.type.startsWith('image/'))
    })
    const hasVideos = filesInGroup.some(f => {
      return (f.type && f.type.startsWith('video/')) || 
             (f.file && f.file.type && f.file.type.startsWith('video/'))
    })
    
    // If group already has both types, it's an error (shouldn't happen, but check anyway)
    if (hasImages && hasVideos) {
      return [t('Recommendations.campaign.setup.media.errors.formatMismatch', { fileName: file.name })]
    }
    
    // If adding video to group with images - error
    if (newFileType === 'video' && hasImages) {
      return [t('Recommendations.campaign.setup.media.errors.formatMismatch', { fileName: file.name })]
    }
    
    // If adding image to group with videos - error
    if (newFileType === 'image' && hasVideos) {
      return [t('Recommendations.campaign.setup.media.errors.formatMismatch', { fileName: file.name })]
    }
    
    // No errors
    return null
  }
    
  const handleFileSelect = async (event, { fileId, placementType, isReplace, fileToReplaceId }) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    // Add path property to match dropzone behavior (react-dropzone adds path to files)
    if (!file.path) {
      file.path = file.name
    }
    
    if (onValidationError) {
      onValidationError([{ file, errors: null }])
    }

    // Reset input
    event.target.value = ''
    
    // Validate file aspect ratio
    const validationErrors = await validateFileForPlacement(file, placementType)
    if (validationErrors) {
      if (onValidationError) {
        onValidationError([{ file, errors: validationErrors }])
      }
      return
    }
    
    // Validate file type compatibility with group
    const targetId = isReplace && fileToReplaceId ? fileToReplaceId : fileId
    const typeValidationErrors = validateFileTypeForGroup(file, targetId, isReplace)
    if (typeValidationErrors) {
      if (onValidationError) {
        onValidationError([{ file, errors: typeValidationErrors }])
      }
      return
    }
    
    // Call parent handler to add or replace file in group
    if (isReplace && fileToReplaceId) {
      // Replace existing file
      if (onReplaceFileInGroup) {
        try {
          await onReplaceFileInGroup(file, fileToReplaceId, placementType)
        } catch (error) {
          const errorMessage = error.message || 'Failed to replace file in group'
          if (onValidationError) {
            onValidationError([{ file, errors: [errorMessage] }])
          }
        }
      }
    } else if (onAddFileToGroup) {
      // Add new file
      try {
        await onAddFileToGroup(file, fileId, placementType)
      } catch (error) {
        const errorMessage = error.message || 'Failed to add file to group'
        if (onValidationError) {
          onValidationError([{ file, errors: [errorMessage] }])
        }
      }
    }
  }
    
  // Group files by placement_group_id and order by group_index
  const getGroupedMedia = () => {
    const groups = new Map()

    // Build groups for both standalone and grouped files
    media.forEach((file) => {
      const groupId = file.placement_group_id || null
      const key = groupId || `single-${file.id}`

      if (!groups.has(key)) {
        groups.set(key, {
          groupId,
          group_index: file.group_index ?? 0,
          files: [],
        })
      }

      const group = groups.get(key)
      group.files.push(file)
      // Ensure group_index is consistent (take the smallest defined value)
      if (file.group_index !== null && file.group_index !== undefined) {
        group.group_index = file.group_index
      }
    })

    const groupedItems = Array.from(groups.values())
      .sort((a, b) => {
        const aIdx = a.group_index ?? 0
        const bIdx = b.group_index ?? 0
        return aIdx - bIdx
      })
      .map((group) => {
        const feedFile = group.files.find((f) => f.placement_type === 'feed') || null
        const reelFile = group.files.find((f) => f.placement_type === 'reel') || null

        return {
          groupId: group.groupId,
          feedFile,
          reelFile,
        }
      })

    return groupedItems
  }

  const getDisplayFile = (item, fileToReplace) => {
    // Determine display file (always first uploaded) and uploading file (for progress)
    let displayFile = item.feedFile || item.reelFile
    let uploadingFile = null
         
    if (item.feedFile && item.reelFile) {
      const feedSuccess = item.feedFile.status === fileStatus.success
      const reelSuccess = item.reelFile.status === fileStatus.success
      const feedUploading = item.feedFile.status === fileStatus.new || item.feedFile.status === fileStatus.uploading
      const reelUploading = item.reelFile.status === fileStatus.new || item.reelFile.status === fileStatus.uploading
           
      // Find uploading file if any
      if (feedUploading) {
        uploadingFile = item.feedFile
      } else if (reelUploading) {
        uploadingFile = item.reelFile
      }
           
      // Determine first uploaded type (feed or reel) by finding the earliest file in the group
      // by order in media array. This handles file replacements correctly - we use the earliest
      // file of each type to determine which type was uploaded first.
      let firstUploadedType = null
      const groupId = item.groupId
           
      if (groupId) {
        // Find all files in this group with the same placement_group_id
        const groupFiles = media.filter(f => f.placement_group_id === groupId)
             
        // Find the earliest feed file and reel file in the group
        const feedFiles = groupFiles.filter(f => f.placement_type === 'feed')
        const reelFiles = groupFiles.filter(f => f.placement_type === 'reel')
             
        let earliestFeedIndex = -1
        let earliestReelIndex = -1
             
        if (feedFiles.length > 0) {
          const feedIndices = feedFiles.map(f => media.findIndex(m => m.id === f.id)).filter(idx => idx !== -1)
          if (feedIndices.length > 0) {
            earliestFeedIndex = Math.min(...feedIndices)
          }
        }
             
        if (reelFiles.length > 0) {
          const reelIndices = reelFiles.map(f => media.findIndex(m => m.id === f.id)).filter(idx => idx !== -1)
          if (reelIndices.length > 0) {
            earliestReelIndex = Math.min(...reelIndices)
          }
        }
             
        // Determine which type was uploaded first
        if (earliestFeedIndex !== -1 && earliestReelIndex !== -1) {
          firstUploadedType = earliestFeedIndex < earliestReelIndex ? 'feed' : 'reel'
        } else if (earliestFeedIndex !== -1) {
          firstUploadedType = 'feed'
        } else if (earliestReelIndex !== -1) {
          firstUploadedType = 'reel'
        }
      } else {
        // For non-grouped files, determine by order in media array
        const feedIndex = media.findIndex(f => f.id === item.feedFile.id)
        const reelIndex = media.findIndex(f => f.id === item.reelFile.id)
        if (feedIndex !== -1 && reelIndex !== -1) {
          firstUploadedType = feedIndex < reelIndex ? 'feed' : 'reel'
        } else if (feedIndex !== -1) {
          firstUploadedType = 'feed'
        } else if (reelIndex !== -1) {
          firstUploadedType = 'reel'
        }
      }
           
      // Display file is always the first uploaded type
      // But if the first type file is being replaced, show the old file (fileToReplace) until upload completes
      if (firstUploadedType === 'feed') {
        // Check if feed file is being replaced - if so, show old file from fileToReplace
        if (
          fileToReplace &&
               fileToReplace.placement_type === 'feed' &&
               fileToReplace.placement_group_id === item.groupId &&
               fileToReplace.id !== item.feedFile?.id &&
               feedUploading
        ) {
          displayFile = fileToReplace
        } else {
          displayFile = item.feedFile
        }
      } else if (firstUploadedType === 'reel') {
        // Check if reel file is being replaced - if so, show old file from fileToReplace
        if (
          fileToReplace &&
               fileToReplace.placement_type === 'reel' &&
               fileToReplace.placement_group_id === item.groupId &&
               fileToReplace.id !== item.reelFile?.id &&
               reelUploading
        ) {
          displayFile = fileToReplace
        } else {
          displayFile = item.reelFile
        }
      } else {
        // Fallback: if no first type determined, use the one that exists or is successful
        // But prioritize the one that is uploading if it exists
        if (feedUploading) {
          displayFile = item.feedFile
        } else if (reelUploading) {
          displayFile = item.reelFile
        } else if (feedSuccess && !reelSuccess) {
          displayFile = item.feedFile
        } else if (reelSuccess && !feedSuccess) {
          displayFile = item.reelFile
        } else {
          displayFile = item.feedFile || item.reelFile
        }
      }
    } else {
      // Single file case - check if it's uploading
      const isUploading = displayFile && (displayFile.status === fileStatus.new || displayFile.status === fileStatus.uploading)
           
      // Check if single file is being replaced
      if (fileToReplace && fileToReplace.id !== displayFile?.id && isUploading) {
        // Show old file (fileToReplace) while new file is uploading
        displayFile = fileToReplace
        uploadingFile = item.feedFile || item.reelFile // The new file that's uploading
      } else if (isUploading) {
        uploadingFile = displayFile
      }
    }

    return {
      displayFile,
      uploadingFile,
    }
  }

  // return
  return {
    handleFileSelect,
    getGroupedMedia,
    getDisplayFile,
  }
}